/**
 * Sub-Customers Redux Slice
 */

import { createSlice } from '@ufc/core'
import type { PayloadAction } from '@ufc/core'
import type { SubCustomer, SubCustomerFilter, PaginationMeta } from '../domain/types'

export interface SubCustomersState {
  items: SubCustomer[]
  selectedSubCustomer: SubCustomer | null
  filter: SubCustomerFilter
  pagination: PaginationMeta
  loading: boolean
  saving: boolean
  error: string | null
}

const initialState: SubCustomersState = {
  items: [],
  selectedSubCustomer: null,
  filter: { page: 1, pageSize: 20 },
  pagination: { page: 1, pageSize: 20, pageCount: 1, total: 0 },
  loading: false,
  saving: false,
  error: null,
}

export const subCustomersSlice = createSlice({
  name: 'subCustomers',
  initialState,
  reducers: {
    setSubCustomers: (state, action: PayloadAction<SubCustomer[]>) => {
      state.items = action.payload
      state.loading = false
      state.error = null
    },
    setPagination: (state, action: PayloadAction<PaginationMeta>) => {
      state.pagination = action.payload
    },
    setFilter: (state, action: PayloadAction<Partial<SubCustomerFilter>>) => {
      state.filter = { ...state.filter, ...action.payload, page: 1 }
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.filter = { ...state.filter, page: action.payload }
    },
    setSelectedSubCustomer: (state, action: PayloadAction<SubCustomer | null>) => {
      state.selectedSubCustomer = action.payload
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setSaving: (state, action: PayloadAction<boolean>) => {
      state.saving = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
      state.loading = false
    },
    addSubCustomer: (state, action: PayloadAction<SubCustomer>) => {
      state.items = [action.payload, ...state.items]
      state.pagination.total += 1
    },
    updateSubCustomerInList: (state, action: PayloadAction<SubCustomer>) => {
      const idx = state.items.findIndex((x) => x.id === action.payload.id)
      if (idx !== -1) state.items[idx] = action.payload
    },
    removeSubCustomer: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((x) => x.id !== action.payload)
      state.pagination.total = Math.max(0, state.pagination.total - 1)
    },
    // Saga trigger actions
    fetchSubCustomers: (_state) => {},
    createSubCustomer: (_state, _action: PayloadAction<{ name: string; phone?: string }>) => {},
    updateSubCustomer: (_state, _action: PayloadAction<{ id: string; name?: string; phone?: string | null }>) => {},
    deleteSubCustomer: (_state, _action: PayloadAction<string>) => {},
  },
})

export const subCustomersActions = subCustomersSlice.actions
export const subCustomersReducer = subCustomersSlice.reducer
